/**
 * eslint-disable @typescript-eslint/no-unsafe-assignment
 *
 * @format
 */

/** @format */

import {
	Box,
	Button,
	Center,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Grid,
	Heading,
	Input,
	InputGroup,
	SimpleGrid,
	Text,
	Textarea,
} from '@chakra-ui/react';
import axios from 'axios';
import {Field, Form, Formik} from 'formik';
import {GraphQLError} from 'graphql';
import {useCallback, useState} from 'react';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import * as Yup from 'yup';
import {FormikField} from '../../../components/FormikField/FormikField';
import {FormikFieldSelect} from '../../../components/FormikField/Select';
import {businessCategoryOptions} from '../../../components/Join/data';
import Layout from '../../../components/Layout';
import {UsernameTooltip} from '../../../components/WhyJoin/Join/YourDetails';
import {TickBullets} from '../../../components/WhyJoin/PackageCards/PackageCardElements';
import getFooter from '../../../lib/api/acf/footer';
import getMainMenu from '../../../lib/api/acf/mainMenu';
import {FeedbackMsgs} from '../../../patterns/CtqChecklist';
import mappingFooter from '../../../patterns/Footer/mappingFooter';
import mappingMainMenu from '../../../patterns/MainMenu/mappingMainMenu';
import {colors} from '../../../styles/js/colors';
import {textStyles} from '../../../styles/js/textStyles';
import {User} from '../../api/user';

type FormValues = {
	businessName: string;
	firstName: string;
	lastName: string;
	website: string;
	email: string;
	phoneNumber: string;
	businessDescription: string;
	username: string;
	password: string;
	confirmPassword: string;
	positionHeld: string;
	typeOfBusiness: string;
	addressLine1: string;
	addressLine2: string;
	addressLine3: string;
	addressLine4: string;
	addressLine5: string;
};

type RegisterUserInput = {
	graphQLErrors: GraphQLError[];
	registerUser: {
		user: User;
	};
};

const Package = ({
	heading,
	paragraph,
	bullets,
}: {
	heading: string;
	paragraph: string;
	bullets: string[];
}) => (
	<Box my={{base: 6, md: 6}} maxW={{base: '100%', md: '22.5rem'}}>
		<Text textStyle='paragraph1LeftDarkGreen'>{heading}</Text>
		<Text textStyle='paragraph2LeftDark' mb={4}>
			{paragraph}
		</Text>
		<TickBullets bullets={bullets} />
	</Box>
);

// allows multiple formats of urls (omit http://, www., etc)
const urlValidation =
	/^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm;

const PartnershipForm = () => {
	const ContactSchema = Yup.object().shape({
		businessName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		firstName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		lastName: Yup.string()
			.min(2, 'Too Short!')
			.max(50, 'Too Long!')
			.required('Required'),
		website: Yup.string()
			.matches(urlValidation, 'Invalid URL')
			.required('Required'),
		email: Yup.string().email('Invalid email').required('Required'),
		phoneNumber: Yup.string()
			.matches(/^[0-9()+-]*$/, 'Invalid phone number')
			.required('Required'),
		businessDescription: Yup.string().required('Required'),
		username: Yup.string()
			.matches(
				/^[a-z0-9_\-@]+$/,
				'Only lowercase letters, numbers, underscores, hyphens, and the "@" character are allowed.',
			)
			.min(1, 'Username should have at least one character.')
			.max(60, 'Username should not exceed 60 characters.') // You can adjust this length as per your requirements
			.required('Username is required.'),
		password: Yup.string()
			.required('Password is required.')
			.min(8, 'Passwords must be at least 8 characters in length.')
			.matches(
				/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
				'Passwords must be at least 8 characters and contain one number or symbol & at least one lower case & upper case character. Passwords are case sensitive.',
			),
		confirmPassword: Yup.string()
			.oneOf([Yup.ref('password'), null], 'Passwords must match')
			.required('Required'),
		positionHeld: Yup.string().required('Required'),
		typeOfBusiness: Yup.string().required('Required'),
		addressLine1: Yup.string().required('Required'),
		addressLine4: Yup.string().required('Required'),
		addressLine5: Yup.string().required('Required'),
	});

	const initialValues: FormValues = {
		businessName: '',
		firstName: '',
		lastName: '',
		website: '',
		email: '',
		phoneNumber: '',
		businessDescription: '',
		username: '',
		password: '',
		confirmPassword: '',
		positionHeld: '',
		typeOfBusiness: '',
		addressLine1: '',
		addressLine2: '',
		addressLine3: '',
		addressLine4: '',
		addressLine5: '',
	};

	const getGlobalMergeVars = (
		formValues: any,
	): Array<{
		name: string;
		content: string;
	}> => {
		const globalMergeVars: any = [
			{
				name: 'BUSINESS_NAME',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.businessName,
			},
			{
				name: 'FIRST_NAME',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.firstName,
			},
			{
				name: 'LAST_NAME',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.lastName,
			},
			{
				name: 'WEBSITE',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.website,
			},
			{
				name: 'EMAIL',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.email,
			},
			{
				name: 'PHONE_NUMBER',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.phoneNumber,
			},
			{
				name: 'BUSINESS_DESCRIPTION',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.businessDescription,
			},
			{
				name: 'USERNAME',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.username,
			},
			{
				name: 'POSITION_HELD',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.positionHeld,
			},
			{
				name: 'TYPE_OF_BUSINESS',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.typeOfBusiness,
			},
			{
				name: 'BUSINESS_ADDRESS1',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.addressLine1,
			},
			{
				name: 'BUSINESS_ADDRESS2',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.addressLine2,
			},
			{
				name: 'BUSINESS_ADDRESS3',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.addressLine3,
			},
			{
				name: 'BUSINESS_CITY',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.addressLine4,
			},
			{
				name: 'BUSINESS_POST_CODE',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: formValues?.addressLine5,
			},
		];
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return globalMergeVars;
	};

	const [success, setSuccess] = useState('');
	const [error, setError] = useState(false);
	const {executeRecaptcha} = useGoogleReCaptcha();

	const addRecaptcha = useCallback(
		async values => {
			if (!executeRecaptcha) {
				// console.log('Execute recaptcha not yet available');
				return;
			}

			const gReCaptchaToken = await executeRecaptcha('enquiryFormContact');
			return gReCaptchaToken;
		},
		[executeRecaptcha],
	);

	const onSubmit = async (
		values: {
			businessName: string;
			firstName: string;
			lastName: string;
			website: string;
			email: string;
			phoneNumber: string;
			businessDescription: string;
			username: string;
			positionHeld: string;
			addressLine1: string;
			addressLine2: string;
			addressLine3: string;
			addressLine4: string;
			addressLine5: string;
		},
		gReCaptchaToken: string,
		resetForm: () => void,
	) => {
		setError(false);
		setSuccess('');

		const message = {
			gReCaptchaToken,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			from_email: process.env.NEXT_PUBLIC_MAILCHIMP_TRANSACTIONAL_MAIL_FROM,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			from_name: 'ASSC Partnership Request',
			subject: 'ASSC Partnership Request',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			template_name: 'ASSC TSP & PFB Enquiry',
			tags: ['ASSC Partnership Request'],
			// eslint-disable-next-line @typescript-eslint/naming-convention
			global_merge_vars: getGlobalMergeVars(values),
			to: [
				{
					email:
						process.env.NEXT_PUBLIC_MAILCHIMP_TRANSACTIONAL_MAIL_TO_MEMBERSHIP,
					type: 'to',
				},
				{
					email: process.env.NEXT_PUBLIC_MAILCHIMP_TRANSACTIONAL_MAIL_TO_ANNA,
					type: 'to',
				},
				{
					email: values.email,
					type: 'cc',
				},
			],
		};

		try {
			const validateReCaptchaResponse = await axios.post(
				'/api/validateReCaptcha',
				message,
			);

			if (validateReCaptchaResponse.status === 200) {
				const registerUser = await axios.post<RegisterUserInput>(
					'/api/member/register',
					values,
				);

				if (registerUser?.data?.graphQLErrors?.length) {
					setError(true);
					throw new Error(
						'An error occurred while register User on partnership.',
					);
				}

				const response = await axios.post('/api/sendEmailTemplate', message);
				if (response.data.success) {
					setSuccess(
						`Submitted ${new Intl.DateTimeFormat('en-UK', {
							day: 'numeric',
							month: 'numeric',
							year: '2-digit',
						}).format(new Date(response.headers.date))}`,
					);
					resetForm();
				}
			} else {
				setError(true);
				throw new Error(
					'An error occurred while validating ReCaptcha on partnership.',
				);
			}
		} catch (error: unknown) {
			console.error(
				'An error occurred while registering and sending email on partnership contact form. ',
				error,
			);
			setError(true);
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={ContactSchema}
			onSubmit={async (values, {resetForm}) => {
				const gReCaptchaToken = await addRecaptcha(values);
				await onSubmit(values, gReCaptchaToken, resetForm);
			}}>
			{({errors, touched, isSubmitting}) => (
				<Form style={{width: '100%'}}>
					<Grid templateColumns={'1fr'} gap={{base: '0', md: 4}}>
						<Field name='businessName'>
							{({field, form}: any) => (
								<FormControl
									isInvalid={Boolean(
										errors.businessName && touched.businessName,
									)}
									pt='8'
									position='relative'>
									<FormLabel
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='businessName'>
										Business Name
									</FormLabel>
									<InputGroup>
										<Input
											{...field}
											id='businessName'
											placeholder='Business name'
										/>
									</InputGroup>
									<FormErrorMessage position='absolute'>
										{errors.businessName}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>
						<Field name='firstName'>
							{({field, form}: any) => (
								<FormControl
									isInvalid={Boolean(errors.firstName && touched.firstName)}
									pt='8'
									position='relative'>
									<FormLabel
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='firstName'>
										First Name
									</FormLabel>
									<InputGroup>
										<Input {...field} id='firstName' placeholder='First name' />
									</InputGroup>

									<FormErrorMessage position='absolute'>
										{errors.firstName}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>
						<Field name='lastName'>
							{({field, form}: any) => (
								<FormControl
									isInvalid={Boolean(errors.lastName && touched.lastName)}
									pt='8'
									position='relative'>
									<FormLabel
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='lastName'>
										Last Name
									</FormLabel>
									<InputGroup>
										<Input {...field} id='lastName' placeholder='Last name' />
									</InputGroup>
									<FormErrorMessage position='absolute'>
										{errors.lastName}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>
						<FormikField
							errors={errors}
							touched={touched}
							label={'Position Held'}
							nameKey={'positionHeld'}
							placeholder={''}
							autoComplete=''
						/>
						<Field name='website'>
							{({field, form}: any) => (
								<FormControl
									isInvalid={Boolean(errors.website && touched.website)}
									pt='8'
									position='relative'>
									<FormLabel
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='website'>
										Website
									</FormLabel>
									<InputGroup>
										<Input {...field} id='website' placeholder='Website' />
									</InputGroup>

									<FormErrorMessage position='absolute'>
										{errors.website}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>
						<Box pt='8'></Box>
						<Text fontSize='3xl' position='sticky'>
							Account Creation
						</Text>
						<Text fontSize='xl'>
							This information will be used to create your ASSC account and
							contact you.
						</Text>
						<Field name='username'>
							{({field, form}: any) => (
								<FormControl
									isInvalid={Boolean(errors.username && touched.username)}
									pt='8'
									position='relative'>
									<FormLabel
										display={'inline-block'}
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='username'>
										Username
									</FormLabel>
									<UsernameTooltip />
									<InputGroup>
										<Input {...field} id='username' placeholder='Username' />
									</InputGroup>
									<FormErrorMessage position='absolute'>
										{errors.username}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>
						<FormikField
							errors={errors}
							touched={touched}
							type={'password'}
							label={'Password'}
							nameKey={'password'}
							autoComplete={'new-password'}
						/>
						<Text sx={textStyles.paragraph2LefGrey}>
							Passwords must be at least 8 characters and contain one number or
							symbol. Passwords are case sensitive.
						</Text>
						<FormikField
							errors={errors}
							touched={touched}
							type={'password'}
							label={'Confirm Password'}
							nameKey={'confirmPassword'}
							autoComplete={'new-password'}
						/>
						<Field name='email' type='email'>
							{({field, form}) => (
								<FormControl
									isInvalid={Boolean(errors.email && touched.email)}
									pt='8'
									position='relative'>
									<FormLabel
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='email'>
										Email Address
									</FormLabel>
									<Input {...field} id='email' placeholder='Email' />
									<FormErrorMessage position='absolute'>
										{errors.email}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>
						<Field name='phoneNumber'>
							{({field, form}: any) => (
								<FormControl
									isInvalid={Boolean(errors.phoneNumber && touched.phoneNumber)}
									pt='8'
									position='relative'>
									<FormLabel
										textStyle={{
											base: 'displayFormLabel',
										}}
										htmlFor='phoneNumber'>
										Phone Number
									</FormLabel>
									<InputGroup>
										<Input
											{...field}
											id='phoneNumber'
											placeholder='Phone number'
										/>
									</InputGroup>

									<FormErrorMessage position='absolute'>
										{errors.phoneNumber}
									</FormErrorMessage>
								</FormControl>
							)}
						</Field>

						<FormikField
							errors={errors}
							touched={touched}
							label={'Business Contact Address'}
							nameKey={'addressLine1'}
							placeholder={'Address Line 1'}
							autoComplete='address-line1'
						/>
						<FormikField
							errors={errors}
							touched={touched}
							nameKey={'addressLine2'}
							placeholder={'Address Line 2'}
							autoComplete='address-line2'
						/>
						<FormikField
							errors={errors}
							touched={touched}
							nameKey={'addressLine4'}
							placeholder={'City'}
							autoComplete='address-level2'
						/>
						<FormikField
							errors={errors}
							touched={touched}
							nameKey={'addressLine3'}
							placeholder={'Local Authority'}
							autoComplete='address-line3'
						/>
						<FormikField
							errors={errors}
							touched={touched}
							nameKey={'addressLine5'}
							placeholder={'Post code'}
							autoComplete='postal-code'
						/>
					</Grid>
					<Field name='businessDescription'>
						{({field, form}) => (
							<FormControl
								isInvalid={Boolean(
									errors.businessDescription && touched.businessDescription,
								)}
								pt='8'
								position='relative'>
								<FormLabel
									textStyle={{
										base: 'displayFormLabel',
									}}
									htmlFor='businessDescription'>
									Business Description
								</FormLabel>
								<Textarea
									{...field}
									id='businessDescription'
									placeholder='Business description'
								/>
								<FormErrorMessage position='absolute'>
									{errors.businessDescription}
								</FormErrorMessage>
							</FormControl>
						)}
					</Field>

					<FormikFieldSelect
						errors={errors}
						touched={touched}
						label={'Type of Business'}
						nameKey={'typeOfBusiness'}
						// multiple={true}
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						options={businessCategoryOptions}
					/>

					<FeedbackMsgs success={success} error={error} />

					<Box pt='8'></Box>
					<Button
						type='submit'
						disabled={isSubmitting}
						mt={{base: '8'}}
						w={{base: '100%', md: 'auto'}}>
						Submit
					</Button>
				</Form>
			)}
		</Formik>
	);
};

function Partnership({
	seo,
	mainMenu,
	footer,
}: {
	seo: any;
	mainMenu: any;
	footer: any;
}) {
	return (
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		<Layout seo={seo} mainMenu={mainMenu} footer={footer}>
			<Box>
				<SimpleGrid columns={{base: 1, md: 2}}>
					<Center>
						<SimpleGrid
							columns={1}
							spacing={'6px'}
							mb={6}
							alignSelf='flex-start'
							justifyContent='left'
							maxW={'25rem'}
							my={{base: 6, md: 6}}
							px={{base: 4, md: 0}}>
							<Heading as='h1' textStyle='display5LeftDark'>
								Partnerships
							</Heading>
							<Package
								heading='Trade Supplier Plus'
								paragraph='Our Trade Supplier Plus programme enables you to expand your reach and impact as a supplier by engaging directly with members of the ASSC.'
								bullets={[
									'Sponsoring our events brings your brand face-to-face with business owners throughout Scotland.',
									'Your features in news items and bespoke newsletters establish your voice and raise your profile.',
									'Engaging our community of business owners directly bolsters your pool of qualified potential clients.',
								]}
							/>
							<Package
								heading='Partners For Business'
								paragraph='Our Partners for Business Scheme is open to industry experts hoping to maximise exposure for their business and contribute to the work of the ASSC.'
								bullets={[
									'Visibility to members digitally and in-person makes engagement with business owners more meaningful.',
									'Aligning your business with our government-endorsed work strengthens your market positioning.',
									'Working alongside the ASSC brings change for the vibrant Scottish self-catering community.',
								]}
							/>
						</SimpleGrid>
					</Center>
					<Center bgColor={colors.tweed[300]}>
						<SimpleGrid
							columns={1}
							spacing={'6px'}
							my={6}
							justifyContent='left'
							maxW={'25rem'}
							px={{base: 4, md: 0}}>
							<Text textStyle='paragraph2LeftDark' mb={10}>
								If you are interested in our partnerships please complete the
								form below:
							</Text>
							<PartnershipForm />
						</SimpleGrid>
					</Center>
				</SimpleGrid>
			</Box>
		</Layout>
	);
}

export async function getServerSideProps() {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let mainMenu = await getMainMenu();
	mainMenu = mappingMainMenu(mainMenu);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let footer = await getFooter();
	footer = mappingFooter(footer);

	return {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		props: {seo: {title: 'Join the ASSC'}, mainMenu, footer},
	};
}

export default Partnership;
